import * as stylex from '@stylexjs/stylex';
import clsx from 'clsx';
import moment from 'moment-timezone';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import promptImage from 'resources/img/hs-prompt-image-2.jpg';
import { openAIAssistantWithExternalPrompt } from '@/aiAssistant/aiAssistant.actions';
import { useAiAssistantDisplayed } from '@/aiAssistant/aiAssistant.store';
import { ContainerWithHTML } from '@/core/ContainerWithHTML.atom';
import { sqWorkbenchStore } from '@/core/core.stores';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { CopyBranchCode } from '@/homescreen/CopyBranchCode.atom';
import { setTrainingPromptDisplay } from '@/homescreen/homescreen.actions';
import { TRAINING_PROMPT_ID, TrainingPromptDisplay } from '@/homescreen/homescreen.constants';
import { useHomeScreenBranchCode } from '@/homescreen/homescreen.store';
import { genAIEnabled, isLearnModeEnabled, isNewHomeScreen2025Enabled } from '@/services/systemConfiguration.utilities';
import { TRACK } from '@/track/track.constants';
import { doTrack } from '@/track/track.service';
import { GET_STARTED_WITH_SEEQ_LINK, TRAINING_TELEMETRY_HOST } from '@/utilities/externalLinks';
import { infoToast, warnToast } from '@/utilities/toast.utilities';
import { faClose, faSparkles } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { Button, ExternalLink, Icon } from '@seeqdev/qomponents';

/**
 * This component renders content of the Training "Get Started" prompt for the Home Screen Page.
 * It DOES NOT include the outer box with the dismiss button.
 */
export const TrainingPromptContent = ({ isModal, onClose }: { isModal?: boolean; onClose?: () => void }) => {
  const { t } = useTranslation();
  const isTrusted = useFluxPath(sqWorkbenchStore, () => sqWorkbenchStore.currentUser.isTrusted);
  const userFirstName = useFluxPath(sqWorkbenchStore, () => sqWorkbenchStore.currentUser.firstName);
  const isAiAssistantDisplayed = useAiAssistantDisplayed();
  const branchCode = useHomeScreenBranchCode();
  const hasBranchCode = Boolean(branchCode?.trim());
  const firstLegacyStep = hasBranchCode
    ? t('HOME_SCREEN.TRAINING.STEP_1', { branchCode: `<b>${branchCode}</b>` })
    : t('HOME_SCREEN.TRAINING.STEP_1_NO_CODE', {
        email: '<a href="mailto:training@seeq.com">training@seeq.com</a>',
      });
  const steps = [
    firstLegacyStep,
    t('HOME_SCREEN.TRAINING.STEP_2', {
      website: `<a href="${TRAINING_TELEMETRY_HOST.href}">${TRAINING_TELEMETRY_HOST.label}</a>`,
    }),
    t('HOME_SCREEN.TRAINING.STEP_3'),
  ];
  const [videoClickCount, setVideoClickCount] = useState(0);
  const [videoStartTime, setVideoStartTime] = useState(moment.now());

  const isNewHomeScreenEnabled = isNewHomeScreen2025Enabled();

  useEffect(() => {
    doTrack(TRACK.TRAINING, 'home screen intro video shown');
  }, []);

  const trackTrainingLinkClick = () => {
    doTrack(TRACK.TRAINING, TRAINING_TELEMETRY_HOST.label, 'open');
  };

  const renderPromptHeader = (header: string, subheader: string) => (
    <div className={stylex.props(homeStyles.cardHeader).className}>
      <h4
        className={clsx(
          'homeScreenPromptHeading',
          stylex.props(
            homeStyles.cardTitle,
            isNewHomeScreenEnabled && homeStyles.sectionTitle,
            !isNewHomeScreenEnabled && homeStyles.sectionTitle,
          ).className,
        )}>
        {header}
      </h4>
      <span>{subheader}</span>
    </div>
  );

  const renderPromptFooter = (header: React.ReactNode, subheader?: string) => (
    <div className={stylex.props(homeStyles.cardFooter).className}>
      <span>{header}</span>
      {subheader && <span>{subheader}</span>}
    </div>
  );

  const renderNewPromptContent = () => (
    <div className={stylex.props(homeStyles.content).className} data-testid="trainingPromptContentNew">
      <div
        className={
          stylex.props(homeStyles.cards, isModal && homeStyles.modalCards, !isModal && homeStyles.inlineCards).className
        }>
        <div className={clsx('homeScreenSectionContainer', stylex.props(homeStyles.card).className)}>
          {renderPromptHeader(t('HOME_SCREEN.TRAINING.HANDS_ON_TRAINING'), t('HOME_SCREEN.TRAINING.REAL_USE_CASES'))}
          <ExternalLink
            href={TRAINING_TELEMETRY_HOST.href}
            onClick={trackTrainingLinkClick}
            xstyle={[homeStyles.imageLink]}>
            <img
              alt={t('HOME_SCREEN.TRAINING.PROMPT_IMAGE_ALT')}
              className={stylex.props(homeStyles.image).className}
              src={promptImage}
            />
          </ExternalLink>
          {renderPromptFooter(
            <>
              <ol className={stylex.props(homeStyles.steps).className}>
                {!isLearnModeEnabled() &&
                  (hasBranchCode ? (
                    <li>
                      <CopyBranchCode text="HOME_SCREEN.TRAINING.COPY_BRANCH_CODE" branchCode={branchCode!} />
                    </li>
                  ) : (
                    <li>{t('HOME_SCREEN.TRAINING.NO_BRANCH_CODE')}</li>
                  ))}
                <li>
                  <ExternalLink
                    label={
                      isLearnModeEnabled() ? t('HOME_SCREEN.TRAINING.GO_TO') : t('HOME_SCREEN.TRAINING.REGISTER_ON')
                    }
                    href={TRAINING_TELEMETRY_HOST.href}
                    onClick={trackTrainingLinkClick}>
                    {TRAINING_TELEMETRY_HOST.label}
                  </ExternalLink>
                </li>
              </ol>
            </>,
          )}
        </div>
        <div className={clsx('homeScreenSectionContainer', stylex.props(homeStyles.card).className)}>
          {renderPromptHeader(t('HOME_SCREEN.TRAINING.WHAT_IS_SEEQ'), t('HOME_SCREEN.TRAINING.EXPLORE'))}
          <video
            className={stylex.props(homeStyles.video).className}
            controls={true}
            autoPlay={false}
            disablePictureInPicture={true}
            controlsList="nodownload"
            onClick={() => {
              // Only the first click tracks "started"; later clicks toggle play/pause and
              // report the duration of the just-finished segment, not another start.
              if (videoClickCount % 2) {
                const endTime = moment.now();
                const duration = endTime - videoStartTime;
                doTrack(TRACK.TRAINING, 'Homescreen video played v2 for', moment(duration).format('mm:ss'));
              }

              if (!videoClickCount) {
                doTrack(TRACK.TRAINING, 'Homescreen video played v2');
              }
              setVideoClickCount(videoClickCount + 1);
              setVideoStartTime(moment.now());
            }}>
            <source src="https://telemetry.seeq.com/seeq-intro-video" />
          </video>
          {renderPromptFooter(
            <div className={stylex.props(homeStyles.videoHelp).className}>
              {t('VIDEO.TROUBLE_VIEWING')}
              <ExternalLink href="https://www.seeq.com/product/our-offerings/" xstyle={[homeStyles.videoHelpLink]}>
                {t('VIDEO.WATCH_HERE')}
              </ExternalLink>
            </div>,
          )}
        </div>
      </div>
    </div>
  );

  const renderLegacyPromptContent = () => (
    <div
      className={stylex.props(homeStyles.content, !isNewHomeScreenEnabled && homeStyles.legacyContent).className}
      data-testid="trainingPromptContent">
      <>
        <h1 className={stylex.props(homeStyles.welcomeTitle).className}>{t('HOME_SCREEN.WELCOME')}</h1>
        <div className={stylex.props(homeStyles.welcomeMessage).className}>
          {t('HOME_SCREEN.NEW_USER_MESSAGE', { name: userFirstName })}
        </div>
      </>
      <div
        className={
          stylex.props(homeStyles.legacyCards, isModal && homeStyles.modalCards, !isModal && homeStyles.inlineCards)
            .className
        }>
        <div className={stylex.props(homeStyles.videoSection, !isModal && homeStyles.homeScreenVideo).className}>
          <video
            className={stylex.props(homeStyles.video, !isModal && homeStyles.inlineVideo).className}
            controls={true}
            autoPlay={false}
            disablePictureInPicture={true}
            controlsList="nodownload"
            onClick={() => {
              // Only the first click tracks "started"; later clicks toggle play/pause and
              // report the duration of the just-finished segment, not another start.
              if (videoClickCount % 2) {
                const endTime = moment.now();
                const duration = endTime - videoStartTime;
                doTrack(TRACK.TRAINING, 'Homescreen video played v2 for', moment(duration).format('mm:ss'));
              }

              if (!videoClickCount) {
                doTrack(TRACK.TRAINING, 'Homescreen video played v2');
              }
              setVideoClickCount(videoClickCount + 1);
              setVideoStartTime(moment.now());
            }}>
            <source src="https://telemetry.seeq.com/seeq-intro-video" />
          </video>
          {renderPromptFooter(
            <div className={stylex.props(homeStyles.legacyVideoHelp).className}>
              {t('VIDEO.TROUBLE_VIEWING')}
              <ExternalLink href={GET_STARTED_WITH_SEEQ_LINK} xstyle={[homeStyles.videoHelpLink]}>
                {t('VIDEO.WATCH_HERE')}
              </ExternalLink>
            </div>,
          )}
        </div>
        <div className={stylex.props(homeStyles.instructions).className}>
          {genAIEnabled() && isTrusted && (
            <div className={stylex.props(homeStyles.assistantAction).className}>
              <Button
                label={t('AI_ASSISTANT.GET_STARTED')}
                icon={faSparkles}
                variant="theme"
                onClick={() => {
                  doTrack(TRACK.AI_ASSISTANT, 'get started prompt clicked');
                  openAIAssistantWithExternalPrompt(
                    { prompt: t('AI_ASSISTANT.GET_STARTED_PROMPT'), agent: 'general' },
                    { wasDisplayed: isAiAssistantDisplayed },
                  );
                  isModal && onClose && onClose();
                }}
              />
            </div>
          )}
          <div className={stylex.props(homeStyles.trainingPrompt).className}>
            {hasBranchCode ? t('HOME_SCREEN.TRAINING.NEXT_BRANCH_PROMPT') : t('HOME_SCREEN.TRAINING.NEXT_PROMPT')}
          </div>
          {hasBranchCode && (
            <div className={stylex.props(homeStyles.branchSubtitle).className}>
              {t('HOME_SCREEN.TRAINING.NEXT_BRANCH_SUBTITLE')}
            </div>
          )}
          <ol className={stylex.props(homeStyles.trainingPromptText).className}>
            {steps.map((step: string, idx: number) => (
              <li key={idx}>
                <ContainerWithHTML content={step} />
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );

  return isNewHomeScreenEnabled ? renderNewPromptContent() : renderLegacyPromptContent();
};

/**
 * This component renders the Training "Get Started" prompt for the Home Screen Page.
 * It includes the outer box with the dismiss button.
 */
export const TrainingPrompt = () => {
  const isNewHomeScreenEnabled = isNewHomeScreen2025Enabled();
  const { t } = useTranslation();

  const renderLegacyPanel = () => (
    <div className={stylex.props(homeStyles.legacyPanel).className}>
      <div className={stylex.props(homeStyles.legacyDismiss).className}>
        <span className={stylex.props(homeStyles.legacyDismissLabel).className}>
          {t('HOME_SCREEN.TRAINING.HIDE_THIS_PANEL')}
        </span>
        <Icon
          onClick={() => {
            setTrainingPromptDisplay(TrainingPromptDisplay.NONE);
            doTrack(TRACK.TRAINING, 'get started prompt closed');
            warnToast({ messageKey: 'HOME_SCREEN.RESUME_TRAINING' });
          }}
          icon={faClose}
          xstyle={[homeStyles.closeIcon, homeStyles.legacyDismissIcon]}
          testId="trainingDismissIcon"
          type={isNewHomeScreenEnabled ? undefined : 'gray'}
        />
      </div>
      <TrainingPromptContent />
    </div>
  );

  const dismissTraining = () => {
    setTrainingPromptDisplay(TrainingPromptDisplay.NONE);
    doTrack(TRACK.TRAINING, 'get started prompt closed');
    infoToast({ messageKey: 'HOME_SCREEN.TRAINING.DISMISS_TRAINING_TEXT' });
  };

  const renderNewPanel = () => (
    <div
      id={TRAINING_PROMPT_ID}
      className={clsx('homeScreenSectionContainer', stylex.props(homeStyles.panel).className)}>
      <div className={stylex.props(homeStyles.panelHeader).className}>
        <h3 className={clsx('homeScreenTrainingTitle', stylex.props(homeStyles.panelTitle).className)}>
          {t('HOME_SCREEN.TRAINING.TRAINING_TITLE')}
        </h3>
        <div className={stylex.props(homeStyles.dismissActions).className}>
          <span className={stylex.props(homeStyles.dismissLabel).className} onClick={dismissTraining}>
            {t('HOME_SCREEN.TRAINING.HIDE_WINDOW')}
          </span>
          <Icon
            onClick={dismissTraining}
            icon={faClose}
            xstyle={[homeStyles.closeIcon, homeStyles.dismissIcon]}
            testId="trainingDismissIcon"
            type={isNewHomeScreenEnabled ? undefined : 'gray'}
          />
        </div>
      </div>
      <TrainingPromptContent />
    </div>
  );
  return isNewHomeScreenEnabled ? renderNewPanel() : renderLegacyPanel();
};

const homeStyles = stylex.create({
  cardHeader: {
    marginBottom: '0.9375rem',
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'flex-start',
  },
  cardTitle: {
    fontWeight: 'bolder',
    marginTop: '0rem',
  },
  sectionTitle: {
    color: 'var(--sq-homescreen-section-title)',
  },
  cardFooter: {
    display: 'flex',
    flexDirection: 'column',
  },
  content: {
    display: 'flex',
    width: '100%',
    flexDirection: 'column',
  },
  cards: {
    display: 'flex',
    width: '100%',
    gap: '1.875rem',
  },
  modalCards: {
    flexDirection: 'column',
  },
  inlineCards: {
    flexDirection: 'row',
  },
  card: {
    paddingTop: 'var(--sq-homescreen-training-padding)',
    paddingRight: 'var(--sq-homescreen-training-padding)',
    paddingBottom: 'var(--sq-homescreen-training-padding)',
    paddingLeft: 'var(--sq-homescreen-training-padding)',
    borderRadius: 10,
    borderWidth: 'var(--sq-homescreen-panel-border-width)',
    borderStyle: 'solid',
    borderColor: 'var(--sq-homescreen-panel-border)',
    backgroundColor: 'var(--sq-homescreen-training-card)',
    display: 'flex',
    width: '50%',
    flexDirection: 'column',
  },
  imageLink: {
    height: '100%',
    maxHeight: '30vh',
    display: 'block',
    borderRadius: '0.375rem',
    position: 'relative',
    overflow: 'hidden',
    textDecoration: 'none',
    width: '100%',
  },
  image: {
    position: 'absolute',
    inset: '0rem',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  },
  steps: {
    paddingLeft: '0.75rem',
    marginBottom: '0rem',
  },
  video: {
    marginBottom: '0.625rem',
    height: '100%',
    width: '100%',
    borderRadius: '0.375rem',
  },
  videoHelp: {
    fontStyle: 'italic',
  },
  videoHelpLink: {
    marginLeft: '0.3125rem',
  },
  legacyContent: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  welcomeTitle: {
    fontWeight: 'bolder',
  },
  welcomeMessage: {
    fontSize: 'larger',
    paddingLeft: '0.9375rem',
    marginRight: '1.25rem',
    marginLeft: '1.25rem',
    textAlign: 'center',
  },
  legacyCards: {
    marginTop: '0.75rem',
    display: 'flex',
    width: '100%',
    justifyContent: 'center',
    gap: '0.25rem',
    borderTopWidth: 1,
    borderTopStyle: 'solid',
    borderTopColor: 'var(--sq-disabled-gray)',
    paddingTop: '0.75rem',
  },
  videoSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingTop: '0.75rem',
    paddingRight: '0.75rem',
    paddingBottom: '0.75rem',
    paddingLeft: '0.75rem',
  },
  homeScreenVideo: {
    maxWidth: '60%',
    maxHeight: '45vh',
  },
  inlineVideo: {
    maxWidth: '35vh',
  },
  legacyVideoHelp: {
    fontSize: '75%',
    lineHeight: '90%',
    marginTop: '0.3125rem',
    fontStyle: 'italic',
  },
  instructions: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  assistantAction: {
    marginBottom: '0.625rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  trainingPrompt: {
    fontSize: 'large',
    fontWeight: 'bold',
  },
  branchSubtitle: {
    marginTop: '0.625rem',
    fontWeight: 700,
  },
  trainingPromptText: {
    fontSize: 'larger',
    paddingLeft: '0.9375rem',
  },
  legacyPanel: {
    position: 'relative',
    marginRight: '1.25rem',
    marginBottom: '1.25rem',
    marginLeft: '1.25rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'var(--sq-disabled-gray)',
    borderRadius: '0.375rem',
  },
  legacyDismiss: {
    position: 'absolute',
    top: '10px',
    right: '10px',
    display: 'flex',
    flexDirection: 'row',
  },
  legacyDismissLabel: {
    marginRight: '0.3125rem',
  },
  closeIcon: { fontSize: '1.5em' },
  legacyDismissIcon: {
    color: { 'default': null, ':hover': 'var(--sq-homescreen-dismiss-hover)' },
    cursor: 'pointer',
  },
  panel: {
    paddingTop: 15,
    paddingRight: 15,
    paddingBottom: 15,
    paddingLeft: 15,
    borderRadius: 10,
    borderWidth: 'var(--sq-homescreen-panel-border-width)',
    borderStyle: 'solid',
    borderColor: 'var(--sq-homescreen-panel-border)',
    backgroundColor: 'var(--sq-homescreen-glass-container)',
    display: 'flex',
    flexDirection: 'column',
  },
  panelHeader: {
    marginTop: '-0.625rem',
    marginBottom: '0.625rem',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
  },
  panelTitle: {
    marginTop: '0rem',
    marginRight: '0rem',
    marginBottom: '0rem',
    marginLeft: '0rem',
  },
  dismissActions: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  dismissLabel: {
    color: 'var(--sq-homescreen-section-action)',
    fontSize: '0.9375rem',
    marginRight: '0.3125rem',
    cursor: 'pointer',
  },
  dismissIcon: {
    color: { 'default': 'var(--sq-homescreen-section-action)', ':hover': 'var(--sq-homescreen-dismiss-hover)' },
    cursor: 'pointer',
  },
});
